import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  CheckCircle2,
  ClipboardCheck,
  FileClock,
  GitBranch,
  HeartHandshake,
  LockKeyhole,
  Pill,
  ShieldCheck,
  Smartphone,
  Stethoscope,
} from 'lucide-react';

type Status = 'ready' | 'partial' | 'blocked';

interface Capability {
  id: string;
  title: string;
  status: Status;
  score: number;
  owner: string;
  evidence: string[];
  nextStep: string;
  actionLabel?: string;
  actionPath?: string;
  icon: React.ReactNode;
}

const statusStyles: Record<Status, string> = {
  ready: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  partial: 'border-amber-200 bg-amber-50 text-amber-800',
  blocked: 'border-slate-200 bg-slate-50 text-slate-700',
};

const statusLabels: Record<Status, string> = {
  ready: 'Implemented',
  partial: 'In progress',
  blocked: 'Requires external access',
};

const capabilities: Capability[] = [
  {
    id: 'carer-app',
    title: 'Carer App',
    status: 'partial',
    score: 72,
    owner: 'Care operations',
    evidence: [
      'Web carer portal active at /caregiver',
      'Mobile package exists with check-in, visits, medications, offline queue, sync status, location and biometrics modules',
      'Local offline indicators and optimistic check-in/out flows exist',
    ],
    nextStep: 'Connect production API, run Expo device testing, then produce iOS/Android builds.',
    actionLabel: 'Open carer portal',
    actionPath: '/caregiver',
    icon: <Smartphone className="h-5 w-5" />,
  },
  {
    id: 'family-app',
    title: 'Family App',
    status: 'partial',
    score: 64,
    owner: 'Family engagement',
    evidence: [
      'Family portal module contains dashboard, activity feed, messages, schedule, care plan, health updates and settings pages',
      'Family portal layout is available for a separate family experience',
      'API contracts exist for activity, notifications, message threads and dashboard data',
    ],
    nextStep: 'Expose protected family routes and connect family-member authentication to real records.',
    actionLabel: 'Open family portal',
    actionPath: '/family-portal',
    icon: <HeartHandshake className="h-5 w-5" />,
  },
  {
    id: 'gp-connect',
    title: 'GP Connect',
    status: 'blocked',
    score: 18,
    owner: 'NHS integration',
    evidence: [
      'GP Connect readiness page is available at /gp-connect',
      'No live NHS GP Connect adapter is configured',
      'Production use requires NHS onboarding, assured supplier workflow, endpoint credentials and data-sharing governance',
      'The application should use a connector layer before any patient-data exchange',
    ],
    nextStep: 'Complete NHS onboarding, Spine/SDS setup, mTLS certificates and clinical safety assurance before live data exchange.',
    actionLabel: 'Open GP Connect',
    actionPath: '/gp-connect',
    icon: <Stethoscope className="h-5 w-5" />,
  },
  {
    id: 'emar',
    title: 'eMAR',
    status: 'partial',
    score: 73,
    owner: 'Medication safety',
    evidence: [
      'eMAR board is active at /clients/client-1/medications',
      'PRN protocols, max daily dose guidance and safety checks are visible in the workflow',
      'MAR audit history records Given, Refused, Held and Missed outcomes with notes and PRN evidence',
      'Administration cannot be saved until safety checks and required PRN/refusal evidence are completed',
    ],
    nextStep: 'Connect eMAR to production database, add medication stock/refill audit, GP/pharmacy review workflow and signed MAR export.',
    actionLabel: 'Open medication page',
    actionPath: '/clients/client-1/medications',
    icon: <Pill className="h-5 w-5" />,
  },
  {
    id: 'cqc',
    title: 'CQC Compliance',
    status: 'partial',
    score: 76,
    owner: 'Registered manager',
    evidence: [
      'CQC Advanced Hub is active at /compliance',
      'Q-Score tracks Safe, Effective, Caring, Responsive and Well-led domains',
      'PIR preparation pack, evidence tagging, complaints tracking and inspection audit trail are implemented',
      'Local compliance deadlines and audit dashboard data are available',
    ],
    nextStep: 'Connect evidence, complaints and audit events to production database with immutable retention and manager sign-off workflows.',
    actionLabel: 'Open compliance',
    actionPath: '/compliance',
    icon: <ClipboardCheck className="h-5 w-5" />,
  },
  {
    id: 'workflows',
    title: 'Custom Workflows',
    status: 'partial',
    score: 71,
    owner: 'Operations',
    evidence: [
      'Workflow Builder is active at /workflow-builder',
      'Templates support triggers, configurable steps, owner roles, due times and required flags',
      'Approval rules, evidence tags, trigger simulation and JSON export are implemented',
      'Client intake, missed medication and complaint workflows exist as configurable examples',
    ],
    nextStep: 'Persist workflow definitions in the production database and connect triggers to real events from visits, eMAR, incidents and complaints.',
    actionLabel: 'Open workflow builder',
    actionPath: '/workflow-builder',
    icon: <GitBranch className="h-5 w-5" />,
  },
  {
    id: 'reporting',
    title: 'Reporting',
    status: 'partial',
    score: 78,
    owner: 'Finance and management',
    evidence: [
      'Pulse Reports Centre is active at /analytics/reports',
      '50+ report templates cover operations, finance, workforce, compliance, family, medication and scheduling',
      'Pulse metrics, profit/loss dashboard, change alerts, saved reports and JSON/CSV exports are implemented',
      'Production reporting still needs live database queries and scheduled delivery jobs',
    ],
    nextStep: 'Connect reports to the production database and add scheduled delivery, permissions, saved filters and warehouse-backed metrics.',
    actionLabel: 'Open reports',
    actionPath: '/analytics/reports',
    icon: <BarChart3 className="h-5 w-5" />,
  },
  {
    id: 'security',
    title: 'Support and Security',
    status: 'partial',
    score: 69,
    owner: 'Information governance',
    evidence: [
      'Enterprise Security Hub is active at /security-enterprise',
      'Protected routes, role checks, API user-context headers and PWA caching are implemented',
      'GDPR, NHS DSPT, ISO 27001 and Cyber Essentials Plus evidence trackers are implemented',
      'Support SLA, backup/restore checklist, monitoring controls and incident response tracker are present',
      'Formal DSPT submission and external ISO/Cyber Essentials Plus certification are still required',
    ],
    nextStep: 'Complete real policies, DPIA, supplier contracts, restore tests, vulnerability scans and external certifications.',
    actionLabel: 'Open settings',
    actionPath: '/security-enterprise',
    icon: <LockKeyhole className="h-5 w-5" />,
  },
];

const readinessScore = Math.round(
  capabilities.reduce((sum, capability) => sum + capability.score, 0) / capabilities.length
);

const completedCount = capabilities.filter((capability) => capability.status === 'ready').length;
const partialCount = capabilities.filter((capability) => capability.status === 'partial').length;
const blockedCount = capabilities.filter((capability) => capability.status === 'blocked').length;

export const EnterpriseReadiness: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
                  Fuchsia Gorleston
                </p>
                <h1 className="text-3xl font-bold text-slate-950">Implementation Readiness</h1>
              </div>
            </div>
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600">
              Production domain: <span className="font-semibold text-slate-900">https://fuchsiagorleston.com/</span>
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase text-slate-500">Score</p>
              <p className="mt-1 text-2xl font-bold text-slate-950">{readinessScore}%</p>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase text-emerald-700">Ready</p>
              <p className="mt-1 text-2xl font-bold text-emerald-900">{completedCount}</p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase text-amber-700">Progress</p>
              <p className="mt-1 text-2xl font-bold text-amber-900">{partialCount}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
              <p className="text-xs font-semibold uppercase text-slate-500">External</p>
              <p className="mt-1 text-2xl font-bold text-slate-950">{blockedCount}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-2">
        {capabilities.map((capability) => (
          <article
            key={capability.id}
            className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
                  {capability.icon}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-950">{capability.title}</h2>
                  <p className="mt-1 text-sm text-slate-500">{capability.owner}</p>
                </div>
              </div>
              <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusStyles[capability.status]}`}>
                {statusLabels[capability.status]}
              </span>
            </div>

            <div className="mt-5">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-slate-700">Readiness</span>
                <span className="font-semibold text-slate-950">{capability.score}%</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-slate-100">
                <div
                  className="h-2 rounded-full bg-[#6c3a80]"
                  style={{ width: `${capability.score}%` }}
                />
              </div>
            </div>

            <div className="mt-5 space-y-2">
              {capability.evidence.map((item) => (
                <div key={item} className="flex gap-2 text-sm leading-6 text-slate-600">
                  <CheckCircle2 className="mt-1 h-4 w-4 flex-shrink-0 text-emerald-600" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-5 rounded-md border border-slate-200 bg-slate-50 p-4">
              <div className="flex gap-2 text-sm leading-6 text-slate-700">
                <FileClock className="mt-1 h-4 w-4 flex-shrink-0 text-slate-500" />
                <span>{capability.nextStep}</span>
              </div>
            </div>

            {capability.actionPath && capability.actionLabel && (
              <button
                type="button"
                onClick={() => navigate(capability.actionPath!)}
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#5e3474]"
              >
                <Activity className="h-4 w-4" />
                {capability.actionLabel}
              </button>
            )}
          </article>
        ))}
      </section>
    </div>
  );
};
